import React, { useState, useEffect } from 'react';
import { Card, Spin, Tooltip, Modal, Tabs, Image, Typography } from 'antd';
import { ExceptionOutlined, ExclamationCircleOutlined, FileImageOutlined } from '@ant-design/icons';
import ObservatoryService from '../services/ObservatoryService';
import JSONVisualizer from './JSONVisualizer';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

/**
 * LatestFeedbackCard - Component to display latest feedback grouped by agent
 * 
 * Shows feedback with three clickable icons:
 * - Paper icon: View feedback (feedback form)
 * - ! icon: View agent data (from apiData in Redis)
 * - Image icon: View screenshot
 * 
 * @param {Object} props
 * @param {Object} props.filters - Filter parameters from Observatory dashboard
 * @param {string} props.filters.timePeriod - Time period filter (e.g., "7d", "24h")
 * @param {Array<string>} props.filters.agentNames - Selected agent names
 */
const LatestFeedbackCard = ({ filters = {} }) => {
  const [loading, setLoading] = useState(true);
  const [feedbackData, setFeedbackData] = useState(null);
  const [error, setError] = useState(null);
  
  // Modal states
  const [structuredModalVisible, setStructuredModalVisible] = useState(false);
  const [unstructuredModalVisible, setUnstructuredModalVisible] = useState(false);
  const [screenshotModalVisible, setScreenshotModalVisible] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [detailedFeedback, setDetailedFeedback] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // JSONVisualizer modal states
  const [jsonVisualizerVisible, setJsonVisualizerVisible] = useState(false);
  const [jsonVisualizerData, setJsonVisualizerData] = useState(null);
  const [jsonVisualizerTitle, setJsonVisualizerTitle] = useState('');
  const [jsonVisualizerType, setJsonVisualizerType] = useState(''); // 'feedback_form' or 'redis_data'

  // Load latest feedback on mount and when filters change
  useEffect(() => {
    loadLatestFeedback();
  }, [filters.timePeriod, filters.agentNames]);

  const loadLatestFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare filters for API call
      const apiFilters = {
        timePeriod: filters.timePeriod,
        agentNames: filters.agentNames || []
      };
      
      console.log('LatestFeedbackCard - Loading feedback with filters:', apiFilters);
      
      const data = await ObservatoryService.getLatestFeedback(apiFilters, 20);
      setFeedbackData(data);
    } catch (err) {
      console.error('Error loading latest feedback:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStructuredFeedbackClick = async (feedback) => {
    setSelectedFeedback(feedback);
    setLoadingDetail(true);
    
    try {
      const detail = await ObservatoryService.getFeedbackDetail(feedback.agent_id, feedback.feedback_id);
      
      console.log('Feedback detail loaded:', detail);
      
      if (detail && detail.feedback_form) {
        setDetailedFeedback(detail);
        setStructuredModalVisible(true);
      } else {
        console.error('No feedback_form in detail:', detail);
      }
    } catch (err) {
      console.error('Error loading feedback detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUnstructuredFeedbackClick = async (feedback) => {
    setSelectedFeedback(feedback);
    setLoadingDetail(true);
    
    try {
      const detail = await ObservatoryService.getFeedbackDetail(feedback.agent_id, feedback.feedback_id);
      
      console.log('Redis data loaded:', detail);
      
      if (detail && detail.redis_data) {
        setJsonVisualizerData(detail.redis_data);
        setJsonVisualizerTitle('Unstructured Feedback - Redis Data (Agent Output)');
        setJsonVisualizerType('redis_data');
        setJsonVisualizerVisible(true);
      } else {
        console.error('No redis_data in detail:', detail);
      }
    } catch (err) {
      console.error('Error loading feedback detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleScreenshotClick = (feedback) => {
    setSelectedFeedback(feedback);
    setScreenshotModalVisible(true);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFeedbackRows = () => {
    if (!feedbackData || !feedbackData.feedback_by_agent) {
      return null;
    }

    const agents = Object.keys(feedbackData.feedback_by_agent);
    
    if (agents.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
          <Text type="secondary">No feedback available</Text>
        </div>
      );
    }

    return agents.map((agentId) => {
      const feedbackItems = feedbackData.feedback_by_agent[agentId];
      
      return (
        <div key={agentId} style={{ marginBottom: '20px' }}>
          {/* Agent Name Header */}
          <div style={{ 
            padding: '8px 12px', 
            background: 'white', 
            color: '#162349',
            fontWeight: '600',
            fontSize: '16px',
            borderRadius: '4px 4px 0 0'
          }}>
            {agentId}
          </div>
          
          {/* Feedback Rows */}
          {feedbackItems.map((feedback, index) => (
            <div 
              key={feedback.feedback_id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto auto',
                gap: '16px',
                padding: '12px 16px',
                borderBottom: index < feedbackItems.length - 1 ? '1px solid #F4F4F4' : 'none',
                background: index % 2 === 0 ? '#fafafa' : 'white',
                alignItems: 'center'
              }}
            >
              {/* Timestamp */}
              <div style={{ fontSize: '13px', color: '#595959' }}>
                {formatTimestamp(feedback.timestamp)}
              </div>
              
              {/* Structured Feedback Icon */}
              <Tooltip title="View Feedback">
                <ExceptionOutlined 
                  style={{ 
                    fontSize: '24px', 
                    color: feedback.options_selected.length > 0 ? '#f5222d' : '#d9d9d9',
                    cursor: feedback.options_selected.length > 0 ? 'pointer' : 'default'
                  }}
                  onClick={() => feedback.options_selected.length > 0 && handleStructuredFeedbackClick(feedback)}
                />
              </Tooltip>
              
              {/* Unstructured Feedback Icon */}
              <Tooltip title="View Agent Data">
                <ExclamationCircleOutlined 
                  style={{ 
                    fontSize: '24px', 
                    color: feedback.has_comments ? '#faad14' : '#d9d9d9',
                    cursor: feedback.has_comments ? 'pointer' : 'default'
                  }}
                  onClick={() => feedback.has_comments && handleUnstructuredFeedbackClick(feedback)}
                />
              </Tooltip>
              
              {/* Screenshot Icon */}
              <Tooltip title="View Screenshot">
                <FileImageOutlined 
                  style={{ 
                    fontSize: '24px', 
                    color: feedback.has_screenshot ? '#1890ff' : '#d9d9d9',
                    cursor: feedback.has_screenshot ? 'pointer' : 'default'
                  }}
                  onClick={() => feedback.has_screenshot && handleScreenshotClick(feedback)}
                />
              </Tooltip>
            </div>
          ))}
        </div>
      );
    });
  };

  const renderStructuredFeedbackModal = () => {
    if (!detailedFeedback || !detailedFeedback.feedback_form) return null;
    
    const form = detailedFeedback.feedback_form;
    
    return (
      <Modal
        title={
          <span style={{ color: '#162349', fontWeight: '600', fontSize: '18px' }}>
            Structured Feedback
          </span>
        }
        open={structuredModalVisible}
        onCancel={() => setStructuredModalVisible(false)}
        footer={null}
        width={600}
      >
        {loadingDetail ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <div>
            {/* Agent Info */}
            <div style={{ 
              marginBottom: '20px', 
              paddingBottom: '16px', 
              borderBottom: '1px solid #F4F4F4' 
            }}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong style={{ color: '#595959' }}>Agent:</Text>
                <Text style={{ marginLeft: '8px', color: '#262626' }}>{form.agent_id}</Text>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <Text strong style={{ color: '#595959' }}>User:</Text>
                <Text style={{ marginLeft: '8px', color: '#262626' }}>
                  {form.username} ({form.user_role})
                </Text>
              </div>
              <div>
                <Text strong style={{ color: '#595959' }}>Timestamp:</Text>
                <Text style={{ marginLeft: '8px', color: '#262626' }}>
                  {formatTimestamp(form.timestamp)}
                </Text>
              </div>
            </div>
            
            {/* Options Selected */}
            <div style={{ marginBottom: '24px' }}>
              <Title level={5} style={{ color: '#162349', marginBottom: '12px' }}>
                Issues Selected
              </Title>
              {form.options_selected && form.options_selected.length > 0 ? (
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '16px', 
                  borderRadius: '6px',
                  border: '1px solid #d9d9d9'
                }}>
                  <ul style={{ 
                    paddingLeft: '20px', 
                    margin: 0,
                    listStyleType: 'disc'
                  }}>
                    {form.options_selected.map((option, index) => (
                      <li key={index} style={{ marginBottom: '8px' }}>
                        <Text style={{ fontSize: '14px', color: '#262626' }}>
                          {option.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div style={{ 
                  background: '#fafafa', 
                  padding: '16px', 
                  borderRadius: '6px',
                  textAlign: 'center',
                  border: '1px dashed #d9d9d9'
                }}>
                  <Text type="secondary" italic>No options selected</Text>
                </div>
              )}
            </div>
            
            {/* Comments */}
            <div>
              <Title level={5} style={{ color: '#162349', marginBottom: '12px' }}>
                Comments
              </Title>
              <div style={{ 
                background: '#f5f5f5',
                padding: '16px', 
                borderRadius: '6px',
                minHeight: '100px',
                border: '1px solid #d9d9d9',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.6'
              }}>
                {form.comments ? (
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {form.comments}
                  </Text>
                ) : (
                  <Text type="secondary" italic>No comments provided</Text>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    );
  };

  const renderScreenshotModal = () => {
    if (!selectedFeedback) return null;
    
    const screenshotUrl = ObservatoryService.getScreenshotUrl(
      selectedFeedback.agent_id,
      selectedFeedback.feedback_id
    );
    
    return (
      <Modal
        title={<span style={{ color: '#162349', fontWeight: '600' }}>Feedback Screenshot</span>}
        open={screenshotModalVisible}
        onCancel={() => setScreenshotModalVisible(false)}
        footer={null}
        width={1000}
        centered
      >
        <div style={{ marginBottom: '16px' }}>
          <Text strong>Agent:</Text>
          <Text style={{ marginLeft: '8px' }}>{selectedFeedback.agent_id}</Text>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <Text strong>Timestamp:</Text>
          <Text style={{ marginLeft: '8px' }}>{formatTimestamp(selectedFeedback.timestamp)}</Text>
        </div>
        
        <div style={{ 
          border: '1px solid #d9d9d9', 
          borderRadius: '4px',
          overflow: 'hidden',
          maxHeight: '70vh'
        }}>
          <Image
            src={screenshotUrl}
            alt="Feedback Screenshot"
            style={{ width: '100%' }}
            preview={false}
          />
        </div>
      </Modal>
    );
  };

  return (
    <>
      <Card
        style={{
          height: '100%',
          minHeight: '500px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
        bodyStyle={{ padding: '0', height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div style={{ 
          padding: '16px 20px', 
          borderBottom: '1px solid #F4F4F4',
          background: '#fafafa'
        }}>
          <Title level={5} style={{ margin: 0, color: 'black' }}>
            Latest Feedback
          </Title>
          {feedbackData && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                Showing {feedbackData.total_count} of {feedbackData.total_available} feedback items
              </Text>
              {feedbackData.filters_applied && (
                <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>
                  Filters: {feedbackData.filters_applied.time_period || 'All time'} | {
                    feedbackData.filters_applied.agent_names === 'all' 
                      ? 'All agents' 
                      : `${feedbackData.filters_applied.agent_names.length} agent(s)`
                  }
                </Text>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflow: 'auto', 
          padding: '16px',
          background: 'white'
        }}>
          {loading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%' 
            }}>
              <Spin size="large" tip="Loading feedback..." />
            </div>
          ) : error ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#f5222d' 
            }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                Error Loading Feedback
              </div>
              <div style={{ fontSize: '14px' }}>{error}</div>
            </div>
          ) : (
            renderFeedbackRows()
          )}
        </div>
      </Card>

      {/* Modals */}
      {renderStructuredFeedbackModal()}
      
      <JSONVisualizer
        visible={jsonVisualizerVisible}
        onClose={() => setJsonVisualizerVisible(false)}
        jsonData={jsonVisualizerData}
        title={jsonVisualizerTitle}
        width={1200}
        height={800}
      />
      
      {renderScreenshotModal()}
    </>
  );
};

export default LatestFeedbackCard;